/* eslint-disable no-console */
import { useMemo, useCallback } from "react";

export function useHardwareCalcs({
  pais,
  selectedYear,
  monthsToShow,
  negociosCarteleria,
  negociosCarteleriaChile,
  negociosCarteleriaPeru,
  costosHardwareFlex,
  costosHardwareFlexAnoAnterior,
  costosHardwareFlexChile,
  costosHardwareFlexPeru,
  costosGestionData,
  getUFValue,
  getDolarValue,
  getDolarValueChile,
  getDolarValuePeru,
}) {
  // ---------------------------------------------------------------------------
  // Helper: calcCostosPorTipo
  // The same pattern is repeated 16 times (4 types x 4 datasets). Single helper.
  // ---------------------------------------------------------------------------
  const calcCostosPorTipo = useCallback((costosArray) => {
    const tipos = ["compra_hardware", "instalacion", "materiales", "otros_costos"];
    const result = {};
    tipos.forEach((tipo) => {
      const mapa = Array(12).fill(0);
      costosArray.forEach((c) => {
        if (c.tipo === tipo) {
          const i = c.mes - 1;
          if (i >= 0 && i < 12) mapa[i] += Number(c.monto) || 0;
        }
      });
      result[tipo] = mapa;
    });
    return result;
  }, []);

  // ---------------------------------------------------------------------------
  // Helper: calcIngresosHardware
  // Parametrized revenue calculation used by all 4 variants.
  // - paisCalc: "Chile" uses UF conversion, "Peru" does not
  // - getDolarFn: the specific dollar function to use
  // - costosGestion: only used for OPEX invoice lookup (null for prior year / Peru)
  // - Default moneda: Chile="UF", Peru="PEN"
  // - getUFValue comes from closure (always same function regardless of variant)
  // ---------------------------------------------------------------------------
  const calcIngresosHardware = useCallback(
    (negocios, year, paisCalc, getDolarFn, costosGestion) => {
      const defaultMoneda = paisCalc === "Chile" ? "UF" : "PEN";
      const mapa = Array(12).fill(0);
      negocios.forEach((n) => {
        if (!n.incluye_hardware || n.eliminada) return;
        if (!["100%", "Activo"].includes(n.estado)) return;
        const moneda = (n.moneda_hardware || defaultMoneda).toUpperCase();
        let yaProcesadoConDesglose = false;

        // CAPEX con desglose
        if (
          n.modalidad === "CAPEX" &&
          n.detalle_pagos_hardware &&
          Array.isArray(n.detalle_pagos_hardware) &&
          n.detalle_pagos_hardware.some((p) => p.fecha_factura && p.monto != null)
        ) {
          n.detalle_pagos_hardware.forEach((pago) => {
            if (!pago.fecha_factura || pago.monto == null) return;
            const fecha = new Date(pago.fecha_factura);
            const mes = fecha.getMonth();
            const año = fecha.getFullYear();
            if (año !== year) return;
            let monto = Number(pago.monto);
            const fechaUsada = pago.fecha_factura.split("T")[0];
            if (moneda === "UF" && paisCalc === "Chile") {
              monto = Math.round(monto * getUFValue(fechaUsada));
            } else if (moneda === "US$" || moneda === "USD") {
              const dolar = getDolarFn(fechaUsada);
              monto = Math.round(monto * dolar);
            }
            mapa[mes] += monto;
          });
          yaProcesadoConDesglose = true;
        }

        // OTROS CAPEX
        if (
          n.modalidad === "CAPEX" &&
          n.detalle_otros_hardware &&
          Array.isArray(n.detalle_otros_hardware) &&
          n.detalle_otros_hardware.length > 0
        ) {
          n.detalle_otros_hardware.forEach((pago) => {
            if (!pago.fecha_factura || pago.monto == null) return;
            const fecha = new Date(pago.fecha_factura);
            const mes = fecha.getMonth();
            const año = fecha.getFullYear();
            if (año !== year) return;
            let monto = Number(pago.monto);
            const fechaUsada = pago.fecha_factura.split("T")[0];
            if (moneda === "UF" && paisCalc === "Chile") {
              monto = Math.round(monto * getUFValue(fechaUsada));
            } else if (moneda === "US$" || moneda === "USD") {
              const dolar = getDolarFn(fechaUsada);
              monto = Math.round(monto * dolar);
            }
            mapa[mes] += monto;
          });
        }

        // CAPEX sin desglose
        if (n.modalidad === "CAPEX" && n.fecha_cierre && !yaProcesadoConDesglose) {
          const fechaCierre = new Date(n.fecha_cierre);
          const mesCierre = fechaCierre.getMonth();
          const añoCierre = fechaCierre.getFullYear();
          if (añoCierre !== year) return;
          let monto = Number(n.monto_hardware) || 0;
          const ultimoDia = new Date(year, mesCierre + 1, 0);
          const fechaStr = ultimoDia.toISOString().split("T")[0];
          if (moneda === "UF" && paisCalc === "Chile") {
            monto = Math.round(monto * getUFValue(fechaStr));
          } else if (moneda === "US$" || moneda === "USD") {
            const dolar = getDolarFn(fechaStr);
            monto = Math.round(monto * dolar);
          }
          mapa[mesCierre] += monto;
        }
        // OPEX - corregido para cruzar anos
        else if (n.modalidad === "OPEX" && n.cuota_hardware_opex && n.fecha_cierre) {
          const fechaCierre = new Date(n.fecha_cierre);
          const mesCierre = fechaCierre.getMonth();
          const añoCierre = fechaCierre.getFullYear();
          const pantallas = Number(n.cantidad_pantallas) || 1;
          const cuotaBase = Number(n.cuota_hardware_opex);
          const totalCuotas = Number(n.num_cuotas) || Number(n.plazo_contrato_meses) || 36;

          let cuotaActual = 0;
          let añoActual = añoCierre;
          let mesActual = mesCierre;

          while (cuotaActual < totalCuotas) {
            if (añoActual === year && mesActual >= 0 && mesActual < 12) {
              let fechaFactura;
              if (costosGestion) {
                const registro = costosGestion.find(
                  (c) =>
                    Number(c.negocio_id) === n.id &&
                    Number(c.mes) === mesActual + 1 &&
                    Number(c.ano) === year &&
                    c.fecha_factura
                );
                const ultimoDia = new Date(year, mesActual + 1, 0);
                fechaFactura = registro?.fecha_factura?.split("T")[0] || ultimoDia.toISOString().split("T")[0];
              } else {
                const ultimoDia = new Date(year, mesActual + 1, 0);
                fechaFactura = ultimoDia.toISOString().split("T")[0];
              }
              let cuotaLocal = cuotaBase * pantallas;
              if (moneda === "UF" && paisCalc === "Chile") {
                cuotaLocal = Math.round(cuotaLocal * getUFValue(fechaFactura));
              } else if (moneda === "US$" || moneda === "USD") {
                const dolar = getDolarFn(fechaFactura);
                cuotaLocal = Math.round(cuotaLocal * dolar);
              }
              mapa[mesActual] += cuotaLocal;
            }

            cuotaActual++;
            mesActual++;
            if (mesActual >= 12) {
              mesActual = 0;
              añoActual++;
            }
            if (añoActual > year + 1) break;
          }
        }
      });
      return mapa;
    },
    [getUFValue]
  );

  // ---------------------------------------------------------------------------
  // Revenue: 4 variants using the parametrized helper
  // ---------------------------------------------------------------------------
  const ingresosHardwareMensual = useMemo(
    () => calcIngresosHardware(negociosCarteleria, selectedYear, pais, getDolarValue, costosGestionData),
    [calcIngresosHardware, negociosCarteleria, selectedYear, pais, getDolarValue, costosGestionData]
  );

  const añoAnteriorHW = selectedYear - 1;
  const ingresosHardwareMensualAnoAnterior = useMemo(
    () => calcIngresosHardware(negociosCarteleria, añoAnteriorHW, pais, getDolarValue, null),
    [calcIngresosHardware, negociosCarteleria, añoAnteriorHW, pais, getDolarValue]
  );

  const ingresosHardwareMensualChile = useMemo(
    () => calcIngresosHardware(negociosCarteleriaChile, selectedYear, "Chile", getDolarValueChile, costosGestionData),
    [calcIngresosHardware, negociosCarteleriaChile, selectedYear, getDolarValueChile, costosGestionData]
  );

  const ingresosHardwareMensualPeru = useMemo(
    () => calcIngresosHardware(negociosCarteleriaPeru, selectedYear, "Perú", getDolarValuePeru, null),
    [calcIngresosHardware, negociosCarteleriaPeru, selectedYear, getDolarValuePeru]
  );

  // ---------------------------------------------------------------------------
  // Costs: 4 datasets x 4 types = 16, using calcCostosPorTipo
  // ---------------------------------------------------------------------------
  const costosCurrent = useMemo(() => calcCostosPorTipo(costosHardwareFlex), [calcCostosPorTipo, costosHardwareFlex]);
  const costosAnoAnterior = useMemo(
    () => calcCostosPorTipo(costosHardwareFlexAnoAnterior),
    [calcCostosPorTipo, costosHardwareFlexAnoAnterior]
  );
  const costosChile = useMemo(
    () => calcCostosPorTipo(costosHardwareFlexChile),
    [calcCostosPorTipo, costosHardwareFlexChile]
  );
  const costosPeru = useMemo(
    () => calcCostosPorTipo(costosHardwareFlexPeru),
    [calcCostosPorTipo, costosHardwareFlexPeru]
  );

  // Destructure into individual arrays for backwards-compatible return values
  const compraHardwareMensual = costosCurrent.compra_hardware;
  const instalacionMensual = costosCurrent.instalacion;
  const materialesMensual = costosCurrent.materiales;
  const otrosCostosMensual = costosCurrent.otros_costos;

  const compraHardwareMensualAnoAnterior = costosAnoAnterior.compra_hardware;
  const instalacionMensualAnoAnterior = costosAnoAnterior.instalacion;
  const materialesMensualAnoAnterior = costosAnoAnterior.materiales;
  const otrosCostosMensualAnoAnterior = costosAnoAnterior.otros_costos;

  const compraHardwareMensualChile = costosChile.compra_hardware;
  const instalacionMensualChile = costosChile.instalacion;
  const materialesMensualChile = costosChile.materiales;
  const otrosCostosMensualChile = costosChile.otros_costos;

  const compraHardwareMensualPeru = costosPeru.compra_hardware;
  const instalacionMensualPeru = costosPeru.instalacion;
  const materialesMensualPeru = costosPeru.materiales;
  const otrosCostosMensualPeru = costosPeru.otros_costos;

  // ---------------------------------------------------------------------------
  // YTDs del bloque B
  // ---------------------------------------------------------------------------
  const ytdIngresosHardware = useMemo(
    () => ingresosHardwareMensual.slice(0, monthsToShow).reduce((a, b) => a + b, 0),
    [ingresosHardwareMensual, monthsToShow]
  );

  const ytdCompraHardware = useMemo(
    () => compraHardwareMensual.slice(0, monthsToShow).reduce((a, b) => a + b, 0),
    [compraHardwareMensual, monthsToShow]
  );

  const ytdInstalacion = useMemo(
    () => instalacionMensual.slice(0, monthsToShow).reduce((a, b) => a + b, 0),
    [instalacionMensual, monthsToShow]
  );

  const ytdMateriales = useMemo(
    () => materialesMensual.slice(0, monthsToShow).reduce((a, b) => a + b, 0),
    [materialesMensual, monthsToShow]
  );

  const ytdOtrosCostosHardware = useMemo(
    () => otrosCostosMensual.slice(0, monthsToShow).reduce((a, b) => a + b, 0),
    [otrosCostosMensual, monthsToShow]
  );

  const ytdMargenHardware = useMemo(
    () => ytdIngresosHardware - (ytdCompraHardware + ytdInstalacion + ytdMateriales + ytdOtrosCostosHardware),
    [ytdIngresosHardware, ytdCompraHardware, ytdInstalacion, ytdMateriales, ytdOtrosCostosHardware]
  );

  // ---------------------------------------------------------------------------
  // Return
  // ---------------------------------------------------------------------------
  return {
    // Revenue
    ingresosHardwareMensual,
    ingresosHardwareMensualAnoAnterior,
    ingresosHardwareMensualChile,
    ingresosHardwareMensualPeru,
    // Costs current
    compraHardwareMensual,
    instalacionMensual,
    materialesMensual,
    otrosCostosMensual,
    // Costs prior year
    compraHardwareMensualAnoAnterior,
    instalacionMensualAnoAnterior,
    materialesMensualAnoAnterior,
    otrosCostosMensualAnoAnterior,
    // Costs Chile
    compraHardwareMensualChile,
    instalacionMensualChile,
    materialesMensualChile,
    otrosCostosMensualChile,
    // Costs Peru
    compraHardwareMensualPeru,
    instalacionMensualPeru,
    materialesMensualPeru,
    otrosCostosMensualPeru,
    // YTDs
    ytdIngresosHardware,
    ytdCompraHardware,
    ytdInstalacion,
    ytdMateriales,
    ytdOtrosCostosHardware,
    ytdMargenHardware,
  };
}
